import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { Alert, AlertDescription } from "../components/ui/alert";
import { HeartHandshake, Search, MessageCircle, UserCheck, AlertTriangle, Settings, Users, Link2, Crown, X, Loader2, ChevronDown, ChevronUp, Baby } from "lucide-react";
import ConnectPersonModal, { waLink } from "../components/ConnectPersonModal";
import ConnectionTemplatesDialog from "../components/ConnectionTemplatesDialog";
import { ConnectionsFilters } from "../components/ConnectionsFilters";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TABS = [
  { value: "nao_conectado", label: "Não conectados" },
  { value: "conectado", label: "Conectados" },
  { value: "todos", label: "Todos" },
];

const SexoDot = ({ sexo }) => (
  <span className={`inline-block w-2 h-2 rounded-full ${sexo === "F" ? "bg-pink-500" : sexo === "M" ? "bg-sky-500" : "bg-gray-300"}`} title={sexo === "F" ? "Feminino" : sexo === "M" ? "Masculino" : "Sexo não informado"} />
);

const ConnectionBadges = ({ conexoes, onClick, personId }) => (
  <div className={`flex flex-wrap gap-1 mt-1 ${onClick ? "cursor-pointer" : ""}`} onClick={onClick} title={onClick ? "Alterar conexão" : undefined} data-testid={onClick ? `assign-mentor-${personId}` : undefined}>
    {conexoes.map((c, i) => {
      const lideres = (c.lideres || []).map(l => l.nome).filter(Boolean).join(", ");
      const label = c.tipo === "lider" ? "Líder" : c.tipo === "direta" ? `Conexão: ${lideres || "—"}` : `${c.origem_nome}${lideres ? ` · ${lideres}` : ""}`;
      const cls = c.tipo === "lider" ? "bg-amber-50 text-amber-700 border-amber-200"
        : c.tipo === "direta" ? "bg-green-50 text-green-700 border-green-200"
        : c.tipo === "ministerio" ? "bg-indigo-50 text-indigo-700 border-indigo-200"
        : "bg-sky-50 text-sky-700 border-sky-200";
      return <Badge key={i} variant="outline" className={`text-[10px] font-normal ${cls}`}>{c.tipo === "lider" && <Crown className="h-2.5 w-2.5 mr-1" />}{label}</Badge>;
    })}
  </div>
);

export default function ConnectionsPage() {
  const { user, isAdmin, currentBranch } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status") || "conectado";
  const [scopeAll, setScopeAll] = useState(searchParams.get("scope") === "all");
  const [q, setQ] = useState("");
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectPerson, setConnectPerson] = useState(null);
  const [devocional, setDevocional] = useState(null);
  const [devLoading, setDevLoading] = useState(true);

  // Devocional do dia é carregado ao abrir a tela; o clique no WhatsApp vira um link direto (sem aba em branco)
  useEffect(() => {
    axios.get(`${API}/connections/devocional-resumo`)
      .then(r => setDevocional(r.data))
      .catch(() => setDevocional(null))
      .finally(() => setDevLoading(false));
  }, []);

  const mensagemPara = (p) => {
    const nome = (p.nome_completo || "").trim().split(" ")[0];
    const partes = [`Olá ${nome}!`];
    if (devocional?.versiculo) partes.push(`A Bíblia diz: ${devocional.versiculo}`);
    if (devocional?.motivacao) partes.push(devocional.motivacao);
    if (devocional?.fechamento) partes.push(devocional.fechamento);
    return partes.join("\n\n");
  };

  // Registra o contato/conexão sem bloquear a abertura do WhatsApp
  const registrarEnvio = (p, texto) => {
    if (summary?.meu_pessoa_id && p.id !== summary.meu_pessoa_id) {
      axios.post(`${API}/connections`, { pessoa_id: p.id, origem: "whatsapp", mensagem: texto })
        .then(() => load()).catch(() => null);
    }
  };
  const [templatesOpen, setTemplatesOpen] = useState(false);

  const scope = scopeAll ? "all" : "mine";
  const canConfig = isAdmin() || user?.permissoes?.configuracoes?.editar;
  const [filters, setFilters] = useState({ ministerio_id: "", sexo: "", criancas: false, tutores: false, sem_contato_dias: 0 });
  const [ministries, setMinistries] = useState([]);
  const filtrosAtivos = Object.values(filters).filter(v => v && v !== 0).length;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await axios.get(`${API}/connections/summary`, { params: { scope } });
      setSummary(s.data);
      if (s.data?.can_view === false) { setItems([]); return; }
      const p = await axios.get(`${API}/connections/people`, { params: { scope, status: filters.tutores ? "todos" : status, ...filters } });
      setItems(p.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar conexões");
    } finally {
      setLoading(false);
    }
  }, [scope, status, filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/connections/settings`).then(r => setSettings(r.data)).catch(() => {});
    axios.get(`${API}/connections/alerts`).then(r => setAlerts(r.data || [])).catch(() => {});
    axios.get(`${API}/ministries`).then(r => setMinistries(r.data || [])).catch(() => {});
  }, []);

  const setStatus = (v) => { searchParams.set("status", v); setSearchParams(searchParams); };

  const removeDirect = async (conn, person) => {
    if (!window.confirm(`Remover sua conexão direta com ${person.nome_completo}?`)) return;
    try {
      await axios.delete(`${API}/connections/${conn.id}`);
      toast.success("Conexão removida");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao remover");
    }
  };

  const filtered = items.filter(i => !q || i.nome_completo?.toLowerCase().includes(q.toLowerCase()));

  if (summary && summary.can_view === false) {
    return (
      <div className="p-6 text-center text-muted-foreground" data-testid="connections-no-access">
        <HeartHandshake className="h-12 w-12 mx-auto mb-2 opacity-40" />
        <p>Seu usuário ainda não está vinculado a um cadastro de pessoa.</p>
      </div>
    );
  }
  const directOnly = summary?.scope === "direct";

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-5xl mx-auto" data-testid="connections-page">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><HeartHandshake className="h-6 w-6 text-primary" /> Igreja Conectada</h1>
          <p className="text-sm text-muted-foreground">Ninguém caminha sozinho: toda pessoa tem uma conexão.</p>
        </div>
        {canConfig && (
          <Button variant="outline" size="sm" onClick={() => setTemplatesOpen(true)} data-testid="connections-templates-btn" className="shrink-0">
            <Settings className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Modelos</span>
          </Button>
        )}
      </div>

      {/* Resumo */}
      <Card data-testid="connections-summary-card">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-end justify-between gap-3">
            <div>
              <p className="text-4xl font-bold text-primary" data-testid="connections-percent">{directOnly ? (summary?.conectados ?? 0) : `${summary?.percentual ?? 0}%`}</p>
              <p className="text-xs text-muted-foreground">{directOnly ? "pessoas conectadas a você" : `da ${scopeAll ? "igreja" : "sua rede"} conectada`}</p>
            </div>
            <div className="flex gap-2 text-right">
              <button onClick={() => setStatus("conectado")} className="px-3 py-1.5 rounded-xl bg-green-50 hover:bg-green-100 transition-colors" data-testid="summary-connected-btn">
                <p className="text-lg font-bold text-green-700 leading-none">{summary?.conectados ?? 0}</p>
                <p className="text-[10px] text-green-700/80">conectados</p>
              </button>
              {!directOnly && (
                <button onClick={() => setStatus("nao_conectado")} className="px-3 py-1.5 rounded-xl bg-orange-50 hover:bg-orange-100 transition-colors" data-testid="summary-not-connected-btn">
                  <p className="text-lg font-bold text-orange-700 leading-none">{summary?.nao_conectados ?? 0}</p>
                  <p className="text-[10px] text-orange-700/80">não conectados</p>
                </button>
              )}
            </div>
          </div>
          {!directOnly && <Progress value={summary?.percentual ?? 0} className="h-2.5" />}
          <div className="flex flex-wrap items-center justify-between gap-2">
            {summary?.can_see_all ? (
              <div className="flex items-center gap-2">
                <Checkbox id="scope-all" checked={scopeAll} onCheckedChange={(v) => { setScopeAll(!!v); searchParams.set("scope", v ? "all" : "mine"); setSearchParams(searchParams); }} data-testid="scope-all-checkbox" />
                <Label htmlFor="scope-all" className="text-xs cursor-pointer text-muted-foreground">Ver todas as pessoas e suas conexões</Label>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground" data-testid="scope-hint">
                {directOnly ? "Você vê apenas as pessoas conectadas a você." : "Você vê as pessoas sob sua liderança e suas conexões."}
              </p>
            )}
            {!directOnly && alerts.length > 0 && (
              <button onClick={() => setShowAlerts(v => !v)} className="flex items-center gap-1 text-xs text-amber-700" data-testid="alerts-toggle-btn">
                <AlertTriangle className="h-3.5 w-3.5" /> {alerts.length} alerta{alerts.length > 1 ? "s" : ""} de liderança
                {showAlerts ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>
            )}
          </div>
          {showAlerts && (
            <Alert className="border-amber-200 bg-amber-50" data-testid="leadership-alerts">
              <AlertDescription className="text-xs text-amber-900 space-y-1">
                <p className="font-medium">Para que o apontamento respeite o sexo, cada ministério/inscrição de conexão precisa de líder homem e líder mulher:</p>
                {alerts.map(a => (
                  <p key={a.id}>• <span className="font-medium">{a.nome}</span> ({a.tipo === "ministerio" ? "ministério" : "inscrição"}) — falta líder {a.falta.map(f => f === "M" ? "masculino" : "feminino").join(" e ")}</p>
                ))}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-2">
        {!directOnly && (
        <div className="flex gap-1.5 p-1 bg-muted/50 rounded-full w-fit overflow-x-auto">
          {STATUS_TABS.map(t => (
            <button
              key={t.value}
              onClick={() => setStatus(t.value)}
              className={`px-3.5 py-1.5 rounded-full text-xs sm:text-sm whitespace-nowrap transition-colors ${status === t.value ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
              data-testid={`status-tab-${t.value}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        )}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar pessoa..." className="pl-9" data-testid="connections-search" />
        </div>
      </div>

      {summary?.can_see_all && (
        <ConnectionsFilters filters={filters} onChange={setFilters} ministries={ministries} count={filtrosAtivos} />
      )}

      {/* Lista */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="connections-empty">
          <Users className="h-12 w-12 mx-auto mb-2 opacity-40" />
          <p>{status === "nao_conectado" ? "Todo mundo aqui já está conectado!" : "Nenhuma pessoa encontrada"}</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="connections-list">
          <p className="text-xs text-muted-foreground">{filtered.length} pessoa{filtered.length > 1 ? "s" : ""}</p>
          {filtered.map(p => {
            const canAssign = summary?.pode_vincular_outros;
            const myDirect = p.conexoes.find(c => c.tipo === "direta" && c.origem_id === summary?.meu_pessoa_id);
            return (
              <div key={p.id} className={`flex items-center gap-3 p-3 rounded-xl border bg-card transition-colors hover:bg-muted/30 ${!p.conectado ? "border-orange-200" : ""}`} data-testid={`connection-person-${p.id}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 font-semibold ${p.conectado ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"}`}>
                  {(p.nome_completo || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button className="font-medium text-sm truncate text-left hover:underline" onClick={() => navigate(`/pessoas?search=${encodeURIComponent(p.nome_completo)}`)} data-testid={`person-name-${p.id}`}>
                      {p.nome_completo}
                    </button>
                    <SexoDot sexo={p.sexo} />
                    {p.crianca && (
                      <Badge className="text-[10px] font-normal bg-sky-100 text-sky-700 border-sky-200 hover:bg-sky-100 gap-1" data-testid={`child-badge-${p.id}`}>
                        <Baby className="h-3 w-3" />Criança{p.idade !== null && p.idade !== undefined ? ` · ${p.idade} anos` : ""}
                      </Badge>
                    )}
                    {!p.crianca && p.dependente && p.idade !== null && (
                      <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground" data-testid={`teen-badge-${p.id}`}>12+ · {p.idade} anos</Badge>
                    )}
                    {filters.tutores && p.conexoes_count > 0 && (
                      <Badge variant="outline" className="text-[10px] font-normal bg-primary/5 text-primary border-primary/20" data-testid={`tutor-badge-${p.id}`}>
                        <Users className="h-3 w-3 mr-1" />conexão de {p.conexoes_count} pessoa{p.conexoes_count > 1 ? "s" : ""}
                      </Badge>
                    )}
                    {p.alerta_sexo && p.conectado && (
                      <span title="Conectado a líder de sexo diferente"><AlertTriangle className="h-3.5 w-3.5 text-amber-500" /></span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {p.dependente && p.responsavel_nome ? <>Resp.: {p.responsavel_nome} · </> : null}{p.telefone || "Sem telefone"}
                    {filters.sem_contato_dias > 0 && <> · último contato: {p.ultimo_contato ? new Date(p.ultimo_contato).toLocaleDateString("pt-BR") : "nunca"}</>}
                  </p>
                  {p.conectado ? (
                    <ConnectionBadges conexoes={p.conexoes} personId={p.id} onClick={canAssign ? () => setConnectPerson(p) : undefined} />
                  ) : canAssign ? (
                    <button type="button" onClick={() => setConnectPerson(p)} className="mt-1" title="Vincular conexão" data-testid={`assign-mentor-${p.id}`}>
                      <Badge variant="outline" className="text-[10px] font-normal bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100 cursor-pointer"><Link2 className="h-2.5 w-2.5 mr-1" />Sem conexão · vincular</Badge>
                    </button>
                  ) : (
                    <Badge variant="outline" className="mt-1 text-[10px] font-normal bg-orange-50 text-orange-700 border-orange-200"><Link2 className="h-2.5 w-2.5 mr-1" />Sem conexão</Badge>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {myDirect && !directOnly && (
                    <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-destructive" title="Remover minha conexão" onClick={() => removeDirect(myDirect, p)} data-testid={`remove-connection-${p.id}`}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                  {p.telefone && (() => {
                    const texto = mensagemPara(p);
                    const link = waLink(p.telefone, texto);
                    return (
                      <Button
                        asChild
                        size="sm"
                        disabled={devLoading}
                        className={`h-9 gap-1.5 ${p.conectado ? "" : "bg-green-600 hover:bg-green-700 text-white"} ${devLoading ? "pointer-events-none opacity-60" : ""}`}
                        variant={p.conectado ? "outline" : "default"}
                        title="Enviar devocional do dia pelo WhatsApp"
                      >
                        <a href={link} target="_blank" rel="noopener noreferrer" onClick={() => registrarEnvio(p, texto)} data-testid={`connect-btn-${p.id}`}>
                          {devLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />}
                          <span className="hidden sm:inline">WhatsApp</span>
                        </a>
                      </Button>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConnectPersonModal
        open={!!connectPerson}
        onOpenChange={(o) => !o && setConnectPerson(null)}
        person={items.find(i => i.id === connectPerson?.id) || connectPerson}
        summary={summary}
        onConnected={load}
      />
      <ConnectionTemplatesDialog open={templatesOpen} onOpenChange={setTemplatesOpen} settings={settings} onSaved={setSettings} />
    </div>
  );
}
